'use client';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

import attributesIllustration from './images/attributes.png';
import customerProfileIllustration from './images/customer-profile.png';
import monthlySubscriptionIllustration from './images/monthly-subscription.png';
import promotionalsIllustration from './images/promotionals.png';
import webButtonIllustration from './images/web-button.svg';
import whyRevenueCatIllustration from './images/why-revenuecat.png';

const illustrations = {
  'customer-profile': {
    src: customerProfileIllustration,
    className: 'max-w-[592px] xl:max-w-[468px] lg:max-w-[478px]',
    width: 592,
    height: 380,
  },
  promotionals: {
    src: promotionalsIllustration,
    className:
      '-mx-4 max-w-[640px] xl:max-w-[504px] xl:-mx-3 lg:max-w-[514px] lg:translate-x-1.5 md:translate-x-0 lg:mx-auto',
    width: 608,
    height: 381,
  },
  'monthly-subscription': {
    src: monthlySubscriptionIllustration,
    className:
      'max-w-[520px] xl:max-w-[402px] xl:-translate-x-[33px] lg:max-w-[412px] lg:-translate-x-0',
    width: 524,
    height: 518,
  },
  attributes: {
    src: attributesIllustration,
    className: 'max-w-[592px] xl:max-w-[468px]',
    width: 592,
    height: 296,
  },
  'why-revenuecat': {
    src: whyRevenueCatIllustration,
    className: '-mx-8 max-w-[712px] xl:-mx-[25px] xl:max-w-[586px] lg:max-w-[526px] lg:mx-auto',
    width: 648,
    height: 322,
  },
  'web-button': {
    src: webButtonIllustration,
    className:
      'relative top-0 left-4 z-10 max-h-[532px] max-w-[620px] xl:left-0 xl:max-h-[440px] xl:max-w-[500px] lg:max-h-[488px] lg:max-w-[587px] md:top-0 md:mb-1 md:mt-8',
    width: 620,
    height: 532,
  },
};

const SectionWithIllustrationAndColor = ({ sectionBgColor, items }) => (
  <section
    className={clsx(
      'safe-paddings relative overflow-hidden py-[180px] xl:py-[120px] lg:py-28 md:py-20',
      {
        'bg-gray-1': sectionBgColor === 'gray',
      }
    )}
  >
    <ul className="container flex flex-col gap-[120px] xl:gap-24 lg:gap-[89px] md:gap-14">
      {items.map(
        (
          {
            title,
            description,
            illustrationName,
            illustrationPosition,
            illustrationBacklight,
            illustrationShadow,
          },
          index
        ) => (
          <li key={index} className="flex items-center justify-between lg:flex-col">
            <div
              className={clsx(
                'max-w-[480px] xl:max-w-[386px] lg:max-w-[478px] lg:text-center md:max-w-[328px]',
                index === 2 && 'xl:max-w-[410px]',
                {
                  'lg:order-last': illustrationPosition === 'left',
                }
              )}
            >
              <h2
                className="text-[48px] font-medium leading-denser tracking-tight-4 text-new-black xl:text-[36px] lg:text-[32px] md:text-[28px]"
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <p
                className={clsx(
                  'mt-3 text-[18px] text-xl font-light leading-snug tracking-tight-2 text-new-gray-1 lg:text-[16px] md:mt-2 md:text-center',
                  "[&_li]:bg-center-left [&_li]:bg-[url('/images/check.svg')] [&_li]:bg-[0_4px] [&_li]:bg-no-repeat [&_li]:pl-[26px] [&_ul]:mt-7 [&_ul]:space-y-[18px] [&_ul]:text-left [&_ul]:font-normal",
                  '[&_code]:whitespace-nowrap [&_code]:rounded-[6px] [&_code]:border [&_code]:border-[#D6DBF5] [&_code]:bg-white [&_code]:p-1 [&_code]:font-sans [&_code]:font-medium [&_code]:shadow-[0px_2px_6px_0px_rgba(43,55,100,0.07)]'
                )}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>

            <div
              className={clsx(
                'relative lg:mt-9 md:mt-8',
                illustrationPosition === 'left' && 'order-first lg:order-last',
                illustrationShadow && 'rounded-[14px] shadow-illustration-shadow'
              )}
              aria-hidden
            >
              <img
                src={illustrations[illustrationName].src}
                className={clsx('relative z-20 w-full', illustrations[illustrationName].className)}
                width={illustrations[illustrationName].width}
                height={illustrations[illustrationName].height}
                loading="lazy"
                alt=""
              />
              {illustrationBacklight && illustrationBacklight === 'green' && (
                <span className="pointer-events-none absolute left-1/4 top-1/2 z-10 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-green-radial opacity-60 blur-[45px] xl:h-[395px] xl:w-[395px] lg:h-[404px] lg:w-[404px] md:h-[277px] md:w-[277px]" />
              )}
              {illustrationBacklight && illustrationBacklight === 'purple-orange' && (
                <span className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[640px] w-[655px] -translate-x-1/2 -translate-y-1/2 xl:h-[491px] xl:w-[502px] lg:h-[503px] lg:w-[515px] md:h-[405px] md:w-[415px]">
                  <span className="absolute bottom-[20%] left-[6%] h-[42%] w-[78%] rotate-[32deg] rounded-full bg-secondary-2 opacity-[0.12] blur-3xl" />
                  <span className="absolute right-[6%] top-[15%] h-[43%] w-[68%] rotate-[32deg] rounded-full bg-[#F25A5A] opacity-[0.05] blur-3xl" />
                </span>
              )}
            </div>
          </li>
        )
      )}
    </ul>
  </section>
);

SectionWithIllustrationAndColor.propTypes = {
  sectionBgColor: PropTypes.string.isRequired,
  items: PropTypes.object.isRequired,
};

export default SectionWithIllustrationAndColor;
